/**
 * /sign-in — Wave 52 high-end redesign.
 *
 * Two-column layout (left form, right hero) per UX brief V2 §7.1.
 * Linear-fast spring submit, Stripe-clear copy, Userdot-warm CTAs.
 * Mobile collapses to single column.
 */
"use client";

import * as React from "react";
import { useRouter, useSearchParams } from "next/navigation";

import { signIn } from "@/lib/auth-client";

export default function SignInPage(): React.JSX.Element {
  const router = useRouter();
  const search = useSearchParams();
  const callbackUrl = search.get("callbackUrl") ?? "/dashboard";

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [pending, setPending] = React.useState(false);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      const res = await signIn.email({ email, password, callbackURL: callbackUrl });
      if (res?.error) {
        setError(res.error.message ?? "Yanlış e-posta veya parola.");
        setPending(false);
        return;
      }
      router.push(callbackUrl);
      router.refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Bilinmeyen hata.");
      setPending(false);
    }
  }

  function autofillDemo() {
    setEmail("demo@commerco.ai");
    setPassword("NiveaDemo2026!");
  }

  return (
    <main className="min-h-dvh w-full grid grid-cols-1 lg:grid-cols-2" data-page="sign-in">
      {/* LEFT — Form */}
      <section className="flex flex-col justify-center px-6 py-10 sm:px-12 lg:px-16 bg-[var(--color-bg-base)]">
        <div className="mx-auto w-full max-w-md">
          {/* Brand monogram */}
          <div className="mb-8 flex items-center gap-2">
            <div
              className="flex h-9 w-9 items-center justify-center rounded-md text-white font-bold text-lg"
              style={{ background: "linear-gradient(135deg, oklch(0.62 0.16 195), oklch(0.46 0.14 195))" }}
              aria-hidden
            >
              c
            </div>
            <span className="text-[var(--text-h4)] font-semibold tracking-tight text-[var(--color-fg-primary)]">
              commerco
            </span>
          </div>

          <h1 className="text-[40px] leading-[1.05] font-bold tracking-tight text-[var(--color-fg-primary)] mb-3">
            AI-Native
            <br />
            <span style={{ background: "linear-gradient(90deg, oklch(0.62 0.16 195), oklch(0.58 0.20 290))", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
              Commerce Intelligence
            </span>
          </h1>
          <p className="text-[var(--text-body-md)] text-[var(--color-fg-secondary)] mb-8">
            E-postanı gir, içeri gel.
          </p>

          <form onSubmit={onSubmit} className="flex flex-col gap-4" aria-label="Giriş formu">
            <label className="flex flex-col gap-1.5">
              <span className="text-[12px] font-medium uppercase tracking-wider text-[var(--color-fg-tertiary)]">
                E-posta
              </span>
              <input
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3.5 py-2.5 text-[15px] text-[var(--color-fg-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent transition-all"
                placeholder="demo@commerco.ai"
                disabled={pending}
              />
            </label>

            <label className="flex flex-col gap-1.5">
              <span className="text-[12px] font-medium uppercase tracking-wider text-[var(--color-fg-tertiary)]">
                Parola
              </span>
              <input
                type="password"
                autoComplete="current-password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3.5 py-2.5 text-[15px] text-[var(--color-fg-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent transition-all"
                disabled={pending}
              />
            </label>

            {error ? (
              <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-[13px] text-red-800 flex items-start gap-2">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="flex-shrink-0 mt-0.5">
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <span>{error}</span>
              </div>
            ) : null}

            <button
              type="submit"
              disabled={pending}
              className="mt-2 rounded-md px-4 py-2.5 text-[15px] font-semibold text-white transition-all duration-150 active:scale-[0.98] disabled:opacity-50"
              style={{ background: pending ? "oklch(0.46 0.14 195)" : "linear-gradient(135deg, oklch(0.62 0.16 195), oklch(0.46 0.14 195))", boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)" }}
            >
              {pending ? "Giriş yapılıyor…" : "Giriş yap →"}
            </button>

            <button
              type="button"
              onClick={autofillDemo}
              className="text-[13px] text-[var(--color-fg-tertiary)] hover:text-[var(--color-fg-primary)] underline underline-offset-2 self-start mt-1"
            >
              Demo hesabını otomatik doldur
            </button>
          </form>

          <p className="mt-8 text-[12px] text-[var(--color-fg-tertiary)]">
            Demo erişimi: <code className="font-mono text-[11px] bg-[var(--color-bg-surface)] px-1.5 py-0.5 rounded">demo@commerco.ai</code> · <code className="font-mono text-[11px] bg-[var(--color-bg-surface)] px-1.5 py-0.5 rounded">NiveaDemo2026!</code>
          </p>
        </div>
      </section>

      {/* RIGHT — Hero / Social proof / KVKK */}
      <section
        className="hidden lg:flex flex-col justify-center px-12 lg:px-16 relative overflow-hidden"
        style={{
          background: "radial-gradient(ellipse at top right, oklch(0.62 0.16 195 / 0.12), transparent 60%), radial-gradient(ellipse at bottom left, oklch(0.58 0.20 290 / 0.08), transparent 50%), oklch(0.97 0.005 195)",
        }}
      >
        {/* Animated accent dots */}
        <div className="absolute top-20 right-20 h-2 w-2 rounded-full bg-[oklch(0.62_0.16_195)] opacity-50" />
        <div className="absolute bottom-32 left-12 h-1.5 w-1.5 rounded-full bg-[oklch(0.58_0.20_290)] opacity-40" />

        <div className="relative max-w-md">
          <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[oklch(0.46_0.14_195)] mb-4">
            Production · 5 marketplaces · Türkiye + CEE
          </div>

          <div className="text-[44px] leading-[1.05] font-bold tracking-tight text-[var(--color-fg-primary)] mb-6">
            5 ülke ·
            <br />
            9 marka ·
            <br />
            <span style={{ background: "linear-gradient(90deg, oklch(0.46 0.14 195), oklch(0.40 0.18 290))", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
              gerçek scrape
            </span>
          </div>

          <p className="text-[var(--text-body-md)] text-[var(--color-fg-secondary)] mb-10 leading-relaxed">
            Trendyol · eMAG RO/HU/BG · Allegro PL. <strong>3.300+ PDP, 6.500+ yorum, 343 AI Visibility skoru</strong> — her satır provenance ile etiketli, sentetik veri yok.
          </p>

          {/* Social proof cards */}
          <div className="flex flex-col gap-3 mb-10">
            <div className="rounded-lg border border-[var(--color-border)] bg-white/60 backdrop-blur px-4 py-3 flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-[oklch(0.45_0.18_240)] flex items-center justify-center text-white font-bold text-sm">B</div>
              <div className="flex-1">
                <div className="text-[13px] font-semibold text-[var(--color-fg-primary)]">Beiersdorf Pilot</div>
                <div className="text-[11px] text-[var(--color-fg-tertiary)]">Nivea + Eucerin · Q2 2026</div>
              </div>
              <span className="text-[10px] font-mono uppercase tracking-wider text-[oklch(0.65_0.16_145)]">Active</span>
            </div>
            <div className="rounded-lg border border-[var(--color-border)] bg-white/60 backdrop-blur px-4 py-3 flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-[oklch(0.60_0.22_20)] flex items-center justify-center text-white font-bold text-sm">L</div>
              <div className="flex-1">
                <div className="text-[13px] font-semibold text-[var(--color-fg-primary)]">L'Oréal Demo</div>
                <div className="text-[11px] text-[var(--color-fg-tertiary)]">Multi-tenant isolation · 100% match</div>
              </div>
              <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-fg-tertiary)]">Workspace 2</span>
            </div>
          </div>

          {/* KVKK trust signal */}
          <div className="border-t border-[var(--color-border)] pt-6">
            <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--color-fg-tertiary)] mb-2">
              KVKK · GDPR Trust
            </div>
            <div className="flex flex-wrap gap-2 text-[11px] text-[var(--color-fg-secondary)]">
              <span className="rounded-full bg-white/60 px-2 py-0.5 border border-[var(--color-border)]">Hetzner Almanya (DE)</span>
              <span className="rounded-full bg-white/60 px-2 py-0.5 border border-[var(--color-border)]">RLS production-grade</span>
              <span className="rounded-full bg-white/60 px-2 py-0.5 border border-[var(--color-border)]">Audit trail 7yr</span>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
